import { Link } from "react-router";
import type { StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { talentName } from "../../../data/queries";
import { ErrorState, Loading } from "../../../components/States";
import { agencyTodos, dueLabel, type Todo } from "../../../model/todos";
import { useDashboardData } from "./data";
import { BeitraegeHinweis } from "./BeitraegeHinweis";
import { t, ta } from "../../../lib/sprache";

/** Tagesgeschäft eines Managers (11_app_agentur/manager-ansicht.html): Aufgaben und eigene Rechte. */
export function ManagerAnsicht({ ws }: { ws: StaffWorkspace }) {
  const { q, error, loading, today, views, retry } = useDashboardData(ws);
  if (error) return <ErrorState error={error} retry={retry} />;
  if (loading) return <Loading />;
  const talents = new Map(q.talents.data!.map((t) => [t.id, t]));
  const brands = new Map(q.brands.data!.map((b) => [b.id, b]));
  const todos: Todo[] = agencyTodos({
    today, deals: q.deals.data!, periods: q.periods.data!, invoices: views,
    talentName: (id) => talentName(id ? talents.get(id) : undefined), brandName: (id) => (id ? brands.get(id)?.name ?? "–" : "–"),
    can: { dun: ws.can("dun"), send: ws.can("send_invoices"), approve: ws.can("approve_invoices"), confirm: ws.can("confirm_payments"), payout: ws.can("trigger_payouts") },
  });
  const names = [...talents.values()].filter((t) => t.status !== "archived").map((t) => talentName(t));
  const scope = ws.membership.sees_all_talents
    ? t("Alle Creator", "All creators")
    : t(`Nur zugewiesene Creator: ${names.length > 1 ? `${names.slice(0, -1).join(", ")} und ${names.at(-1)}` : names[0] ?? "keine"}`, `Assigned creators only: ${names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : names[0] ?? "none"}`);
  const allowed = [
    ws.can("approve_invoices") && t("Rechnungen prüfen und freigeben", "review and approve invoices"),
    ws.can("send_invoices") && t("an Marken senden", "send to brands"),
    ws.can("dun") && t("Zahlungen erinnern", "send payment reminders"),
    ws.can("confirm_payments") && t("Zahlungen bestätigen", "confirm payments"),
    ws.can("trigger_payouts") && t("Auszahlungen ausführen", "make payouts"),
  ].filter(Boolean) as string[];
  const locked = [
    !ws.can("dun") && t("Mahnung senden", "Send dunning notice"),
    !ws.can("confirm_payments") && t("Zahlung bestätigen", "Confirm payment"),
    !ws.can("trigger_payouts") && t("Auszahlung", "Payout"),
  ].filter(Boolean) as string[];
  return (
    <main id="main" className="content">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")}</div>
          <h1>{t("Dein Tagesgeschäft", "Your daily work")}</h1>
          <p></p>
        </div>
      </div>
      <div className="notice">{t("Manager · ", "Manager · ")}{ws.name} · {scope}</div>
      <BeitraegeHinweis orgId={ws.org.org_id} today={today} />
      <section className="panel stack">
        <h3>{t("Heute", "Today")}</h3>
        {todos.map((t) => (
          <Link key={t.key} className="task" to={t.to}>
            <div>
              <strong>{t.title}</strong>
              <p>{t.detail}</p>
            </div>
            <span className={`pill ${t.tone}`}><span className="pill-zeichen">{t.tone === "error" ? "!" : "◷"}</span> {dueLabel(t.due, today)}</span>
          </Link>
        ))}
        {todos.length === 0 && <p>{t("Für heute ist alles erledigt. Neue Aufgaben erscheinen hier automatisch.", "All done for today. New tasks appear here automatically.")}</p>}
      </section>
      <section className="panel stack">
        <h3>{t("Deine Möglichkeiten", "Your options")}</h3>
        <p>{allowed.length ? t(`${allowed.join(", ").replace(/, ([^,]*)$/, " und $1")} sind für dich verfügbar.`, `${allowed.join(", ").replace(/, ([^,]*)$/, " and $1")} are available to you.`) : t("Für dein Konto sind keine Finanzaktionen freigegeben.", "No financial actions are enabled for your account.")}</p>
        {locked.length > 0 && (
          <>
            <div className="row">
              {locked.map((l) => <button key={l} className="" disabled aria-describedby="rights-note">{l}</button>)}
            </div>
            <p id="rights-note">{t("Diese Aktionen sind für dein Konto nicht freigegeben. Wende dich an deinen Admin.", "These actions are not permitted for your account. Contact your admin.")}</p>
          </>
        )}
      </section>
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.")}</p>}
    </main>
  );
}
